// ============================================================
// CasosConvivenciaPage — Listado + creación de casos.
// Detalle simplificado en modal (resumen + actores + cerrar caso + SíseVe).
// ============================================================

import React, { useEffect, useState } from 'react';
import { I, UserAvatar } from 'components/ui';
import { useAuth } from 'contexts/AuthContext';
import BuscadorMatricula from 'components/psicologia/BuscadorMatricula';
import { casoConvivenciaService } from 'services/casoConvivenciaService';
import {
  CasoConvivenciaResponse,
  CasoConvivenciaPayload,
  CasoEstadoPayload,
  ActorCasoPayload,
  TipoIncidente,
  GravedadCaso,
  EstadoCaso,
  RolActor,
  TIPO_INCIDENTE_LABEL,
  GRAVEDAD_LABEL,
  GRAVEDAD_COLOR,
  ESTADO_CASO_LABEL,
  ESTADO_CASO_COLOR,
  ROL_ACTOR_LABEL,
  ROL_ACTOR_COLOR,
} from 'types/psicologia.types';
import { MatriculaResponse } from 'types/matricula.types';

const TIPOS:      TipoIncidente[] = ['FISICA','PSICOLOGICA','SEXUAL','CIBERBULLYING','HURTO','DISCRIMINACION','ARMAS_DROGAS','OTRO'];
const GRAVEDADES: GravedadCaso[]  = ['LEVE', 'MODERADA', 'GRAVE'];
const ESTADOS:    EstadoCaso[]    = ['ABIERTO', 'EN_TRATAMIENTO', 'CERRADO'];
const ROLES:      RolActor[]      = ['AGRESOR', 'VICTIMA', 'TESTIGO'];

const Spinner: React.FC<{ size?: number }> = ({ size = 6 }) => (
  <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const CasosConvivenciaPage: React.FC = () => {
  const { user } = useAuth();
  const rol = user?.rol;
  const puedeGestionar = rol === 'PSICOLOGO' || rol === 'ADMIN' || rol === 'DIRECTOR';

  const [casos, setCasos] = useState<CasoConvivenciaResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [filtroEstado, setFiltroEstado] = useState<EstadoCaso | ''>('');
  const [filtroGravedad, setFiltroGravedad] = useState<GravedadCaso | ''>('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [graveSinSiseVe, setGraveSinSiseVe] = useState(false);

  const [showCrear, setShowCrear] = useState(false);
  const [verDetalle, setVerDetalle] = useState<CasoConvivenciaResponse | null>(null);

  const cargar = async () => {
    setLoading(true); setError(null);
    try {
      const data = await casoConvivenciaService.listar({
        estado:   filtroEstado   || undefined,
        gravedad: filtroGravedad || undefined,
        desde:    desde || undefined,
        hasta:    hasta || undefined,
      });
      setCasos(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar casos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filtroEstado, filtroGravedad, desde, hasta]);

  const filtrados = React.useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return casos.filter(c => {
      if (graveSinSiseVe && !(c.gravedad === 'GRAVE' && !c.reportadoSiseVe)) return false;
      if (q) {
        const hay = [
          c.descripcion, c.reportadoPorNombres, c.reportadoPorApellidos,
          ...(c.actores ?? []).map(a => `${a.estudianteNombres} ${a.estudianteApellidos} ${a.estudianteDni}`),
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [casos, busqueda, graveSinSiseVe]);

  const onCreated = (c: CasoConvivenciaResponse) => {
    setCasos(prev => [c, ...prev]);
    setShowCrear(false);
  };

  const onUpdated = (c: CasoConvivenciaResponse) => {
    setCasos(prev => prev.map(x => x.idCaso === c.idCaso ? c : x));
    setVerDetalle(c);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Convivencia escolar</h2>
          <p className="text-slate-400 text-sm mt-0.5">Casos de incidentes y reporte SíseVe</p>
        </div>
        <button
          onClick={() => setShowCrear(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white text-sm font-bold"
        >
          <I.Plus size={14} /> Reportar caso
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 grid md:grid-cols-2 lg:grid-cols-6 gap-3">
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Estado</label>
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value as EstadoCaso | '')}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
            <option value="">Todos</option>
            {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_CASO_LABEL[e]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Gravedad</label>
          <select value={filtroGravedad} onChange={e => setFiltroGravedad(e.target.value as GravedadCaso | '')}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
            <option value="">Todas</option>
            {GRAVEDADES.map(g => <option key={g} value={g}>{GRAVEDAD_LABEL[g]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Desde</label>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Hasta</label>
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
        </div>
        <div className="lg:col-span-2">
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Búsqueda</label>
          <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="Estudiante, descripción, reportante…"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
        </div>
        <label className="lg:col-span-6 flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={graveSinSiseVe} onChange={e => setGraveSinSiseVe(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500" />
          <span className="font-semibold text-red-700">Mostrar solo casos GRAVES sin reportar a SíseVe</span>
        </label>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">{error}</div>}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading && casos.length === 0 ? (
          <div className="flex justify-center py-16 text-indigo-700"><Spinner size={8} /></div>
        ) : filtrados.length === 0 ? (
          <p className="text-slate-400 text-sm py-16 text-center">No hay casos con los filtros aplicados</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Fecha', 'Tipo', 'Gravedad', 'Estado', 'Actores', 'Reportado por', 'SíseVe', ''].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtrados.map(c => (
                  <tr key={c.idCaso} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{c.fechaIncidente}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{TIPO_INCIDENTE_LABEL[c.tipoIncidente]}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${GRAVEDAD_COLOR[c.gravedad]}`}>
                        {GRAVEDAD_LABEL[c.gravedad]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${ESTADO_CASO_COLOR[c.estado]}`}>
                        {ESTADO_CASO_LABEL[c.estado]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {(c.actores ?? []).length} estudiante{(c.actores ?? []).length !== 1 ? 's' : ''}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {c.reportadoPorApellidos}, {c.reportadoPorNombres}
                      <span className="text-slate-400 ml-1">({c.reportadoPorRol})</span>
                    </td>
                    <td className="px-4 py-3">
                      {c.reportadoSiseVe ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700">
                          <I.CheckCircle size={12} /> Sí
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setVerDetalle(c)}
                        className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold inline-flex items-center gap-1">
                        <I.Eye size={12} /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCrear && (
        <CrearCasoModal onClose={() => setShowCrear(false)} onCreated={onCreated} />
      )}
      {verDetalle && (
        <DetalleCasoModal
          caso={verDetalle}
          puedeGestionar={puedeGestionar}
          onClose={() => setVerDetalle(null)}
          onUpdated={onUpdated}
        />
      )}
    </div>
  );
};

// ── Modal: Crear caso ─────────────────────────────────────────

const CrearCasoModal: React.FC<{
  onClose: () => void;
  onCreated: (c: CasoConvivenciaResponse) => void;
}> = ({ onClose, onCreated }) => {
  const [fechaIncidente, setFechaIncidente] = useState(new Date().toISOString().slice(0, 10));
  const [tipo, setTipo]                     = useState<TipoIncidente>('FISICA');
  const [descripcion, setDescripcion]       = useState('');
  const [gravedad, setGravedad]             = useState<GravedadCaso>('MODERADA');
  const [acciones, setAcciones]             = useState('');
  const [actores, setActores] = useState<{ matricula: MatriculaResponse; rolActor: RolActor }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [picker, setPicker] = useState<MatriculaResponse | null>(null);
  const [pickerRol, setPickerRol] = useState<RolActor>('AGRESOR');

  const agregarActor = () => {
    if (!picker) { setError('Selecciona un estudiante para agregar.'); return; }
    if (actores.some(a => a.matricula.idMatricula === picker.idMatricula && a.rolActor === pickerRol)) {
      setError('Ese estudiante ya está agregado con ese rol.'); return;
    }
    setActores(prev => [...prev, { matricula: picker, rolActor: pickerRol }]);
    setPicker(null);
    setError(null);
  };

  const quitarActor = (idMatricula: number, rolActor: RolActor) => {
    setActores(prev => prev.filter(a => !(a.matricula.idMatricula === idMatricula && a.rolActor === rolActor)));
  };

  const enviar = async () => {
    if (descripcion.trim().length < 10) { setError('Describe el incidente con al menos 10 caracteres.'); return; }
    if (actores.length === 0) { setError('Agrega al menos un estudiante involucrado.'); return; }
    const payload: CasoConvivenciaPayload = {
      fechaIncidente,
      tipoIncidente: tipo,
      descripcion: descripcion.trim(),
      gravedad,
      accionesTomadas: acciones.trim() || undefined,
      actores: actores.map(a => ({ matriculaId: a.matricula.idMatricula, rolActor: a.rolActor })),
    };
    setSubmitting(true); setError(null);
    try {
      const created = await casoConvivenciaService.crear(payload);
      onCreated(created);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al registrar caso');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-head">
          <div>
            <h2>Reportar caso de convivencia</h2>
            <div className="sub">Registra el incidente y los estudiantes involucrados</div>
          </div>
          <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
        </div>
        <div className="modal-body grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="field">
              <label>Fecha del incidente <span className="required">*</span></label>
              <input type="date" className="input" value={fechaIncidente} max={new Date().toISOString().slice(0, 10)}
                onChange={e => setFechaIncidente(e.target.value)} />
            </div>
            <div className="field">
              <label>Tipo de incidente</label>
              <select className="input" value={tipo} onChange={e => setTipo(e.target.value as TipoIncidente)}>
                {TIPOS.map(t => <option key={t} value={t}>{TIPO_INCIDENTE_LABEL[t]}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label>Gravedad</label>
            <div className="flex gap-2">
              {GRAVEDADES.map(g => (
                <button key={g} type="button" onClick={() => setGravedad(g)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all
                    ${gravedad === g ? GRAVEDAD_COLOR[g] : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                  {GRAVEDAD_LABEL[g]}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label>Descripción <span className="required">*</span></label>
            <textarea className="input" rows={3} value={descripcion} onChange={e => setDescripcion(e.target.value)}
              maxLength={2000} placeholder="Describe los hechos relevantes" />
          </div>

          <div className="field">
            <label>Acciones tomadas (opcional)</label>
            <textarea className="input" rows={2} value={acciones} onChange={e => setAcciones(e.target.value)}
              maxLength={1000} placeholder="¿Qué se hizo de inmediato? (separar estudiantes, llamar a tutor, etc.)" />
          </div>

          {/* Actores */}
          <div className="border border-slate-200 rounded-xl p-3 space-y-2">
            <p className="text-xs font-bold text-slate-500 uppercase">Estudiantes involucrados <span className="text-red-500">*</span></p>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[240px]">
                <BuscadorMatricula selected={picker} onSelected={setPicker} onClear={() => setPicker(null)} />
              </div>
              <select value={pickerRol} onChange={e => setPickerRol(e.target.value as RolActor)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                {ROLES.map(r => <option key={r} value={r}>{ROL_ACTOR_LABEL[r]}</option>)}
              </select>
              <button type="button" onClick={agregarActor}
                className="px-3 py-2 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-xs font-bold">
                Agregar
              </button>
            </div>
            {actores.length > 0 && (
              <ul className="space-y-1.5">
                {actores.map(a => (
                  <li key={`${a.matricula.idMatricula}-${a.rolActor}`}
                    className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${ROL_ACTOR_COLOR[a.rolActor]}`}>
                      {ROL_ACTOR_LABEL[a.rolActor]}
                    </span>
                    <span className="flex-1 text-slate-700">
                      {a.matricula.estudianteApellidos}, {a.matricula.estudianteNombres}
                      <span className="text-slate-400 text-xs ml-2">DNI {a.matricula.estudianteDni}</span>
                    </span>
                    <button type="button" onClick={() => quitarActor(a.matricula.idMatricula, a.rolActor)}
                      className="p-1 text-slate-400 hover:text-red-600">
                      <I.X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={enviar} disabled={submitting}>
            {submitting ? 'Registrando…' : 'Registrar caso'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal: Detalle del caso ───────────────────────────────────

const DetalleCasoModal: React.FC<{
  caso: CasoConvivenciaResponse;
  puedeGestionar: boolean;
  onClose: () => void;
  onUpdated: (c: CasoConvivenciaResponse) => void;
}> = ({ caso, puedeGestionar, onClose, onUpdated }) => {
  const [showCerrar, setShowCerrar] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toggleSiseVe = async () => {
    setSubmitting(true);
    try {
      const updated = await casoConvivenciaService.marcarSiseVe(caso.idCaso, !caso.reportadoSiseVe);
      onUpdated(updated);
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
          <div className="modal-head">
            <div>
              <h2>Caso #{caso.idCaso}</h2>
              <div className="sub">{caso.fechaIncidente} · {TIPO_INCIDENTE_LABEL[caso.tipoIncidente]}</div>
            </div>
            <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
          </div>

          <div className="modal-body space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${GRAVEDAD_COLOR[caso.gravedad]}`}>
                {GRAVEDAD_LABEL[caso.gravedad]}
              </span>
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${ESTADO_CASO_COLOR[caso.estado]}`}>
                {ESTADO_CASO_LABEL[caso.estado]}
              </span>
              {caso.reportadoSiseVe && (
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full border bg-green-100 text-green-700 border-green-200 inline-flex items-center gap-1">
                  <I.CheckCircle size={11} /> SíseVe reportado
                </span>
              )}
            </div>

            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Descripción</p>
              <p className="text-sm text-slate-700 whitespace-pre-line">{caso.descripcion}</p>
            </div>

            {caso.accionesTomadas && (
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Acciones tomadas</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{caso.accionesTomadas}</p>
              </div>
            )}

            {caso.compromisoFamiliar && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-[11px] font-bold text-blue-700 uppercase mb-1">Compromiso familiar</p>
                <p className="text-sm text-blue-900 whitespace-pre-line">{caso.compromisoFamiliar}</p>
              </div>
            )}

            {/* Actores */}
            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-2">Estudiantes involucrados</p>
              {(caso.actores ?? []).length === 0 ? (
                <p className="text-xs text-slate-400">Sin actores registrados</p>
              ) : (
                <ul className="space-y-2">
                  {caso.actores.map(a => (
                    <li key={a.idActor} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                      <UserAvatar
                        idUsuario={a.estudianteId}
                        fotoPerfil={a.estudianteFotoPerfil}
                        nombres={a.estudianteNombres}
                        apellidos={a.estudianteApellidos}
                        size={36}
                        rounded="xl"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate">
                          {a.estudianteApellidos}, {a.estudianteNombres}
                        </p>
                        <p className="text-xs text-slate-500">
                          {a.gradoNombre} {a.seccionNombre} · DNI {a.estudianteDni}
                        </p>
                      </div>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${ROL_ACTOR_COLOR[a.rolActor]}`}>
                        {ROL_ACTOR_LABEL[a.rolActor]}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="text-xs text-slate-400 space-y-0.5">
              <p>Reportado por <span className="font-semibold text-slate-600">{caso.reportadoPorApellidos}, {caso.reportadoPorNombres}</span> ({caso.reportadoPorRol})</p>
              {caso.fechaCierre && <p>Cerrado el {caso.fechaCierre}</p>}
              {caso.fechaReporteSiseVe && <p>Reportado a SíseVe el {caso.fechaReporteSiseVe}</p>}
            </div>
          </div>

          {puedeGestionar && (
            <div className="modal-foot flex-wrap gap-2">
              <button onClick={toggleSiseVe} disabled={submitting}
                className={`btn ${caso.reportadoSiseVe ? 'btn-ghost' : 'btn-primary'}`}>
                {caso.reportadoSiseVe ? 'Desmarcar SíseVe' : 'Marcar reportado a SíseVe'}
              </button>
              {caso.estado !== 'CERRADO' && (
                <button onClick={() => setShowCerrar(true)}
                  className="btn btn-primary">
                  Cerrar caso
                </button>
              )}
              <button onClick={onClose} className="btn btn-ghost ml-auto">Cerrar ventana</button>
            </div>
          )}
        </div>
      </div>

      {showCerrar && (
        <CerrarCasoModal
          caso={caso}
          onClose={() => setShowCerrar(false)}
          onUpdated={(c) => { onUpdated(c); setShowCerrar(false); }}
        />
      )}
    </>
  );
};

const CerrarCasoModal: React.FC<{
  caso: CasoConvivenciaResponse;
  onClose: () => void;
  onUpdated: (c: CasoConvivenciaResponse) => void;
}> = ({ caso, onClose, onUpdated }) => {
  const [acciones, setAcciones] = useState(caso.accionesTomadas ?? '');
  const [compromiso, setCompromiso] = useState(caso.compromisoFamiliar ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = async () => {
    const payload: CasoEstadoPayload = {
      estado:             'CERRADO',
      accionesTomadas:    acciones.trim() || undefined,
      compromisoFamiliar: compromiso.trim() || undefined,
    };
    setSubmitting(true); setError(null);
    try {
      const updated = await casoConvivenciaService.actualizarEstado(caso.idCaso, payload);
      onUpdated(updated);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cerrar caso');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div>
            <h2>Cerrar caso</h2>
            <div className="sub">Caso #{caso.idCaso} · La fecha de cierre se setea automáticamente</div>
          </div>
          <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
        </div>
        <div className="modal-body grid gap-3">
          <div className="field">
            <label>Acciones tomadas (opcional)</label>
            <textarea className="input" rows={3} value={acciones} onChange={e => setAcciones(e.target.value)} maxLength={1000} />
          </div>
          <div className="field">
            <label>Compromiso familiar (opcional)</label>
            <textarea className="input" rows={3} value={compromiso} onChange={e => setCompromiso(e.target.value)}
              maxLength={1000} placeholder="Acuerdo o compromiso firmado con la familia" />
          </div>
          {error && <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={submitting}>
            {submitting ? 'Cerrando…' : 'Confirmar cierre'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CasosConvivenciaPage;

// (sin uso)
const _UNUSED: ActorCasoPayload = { matriculaId: 0, rolActor: 'TESTIGO' };
void _UNUSED;
